import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  getApplicationsForJob,
  updateApplicationStatus,
  exportApplicationsPDF,
  exportApplicationsExcel,
} from "../../../services/job-applications";
import Header from "../../../components/layout/Header";
import "../styles/ApplicationsTable.css";

const SubmittedApplications = () => {
  const { token } = useAuth();
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    getApplicationsForJob(token, jobId)
      .then((res) => setApplications(res.data))
      .catch(() => setApplications([]))
      .finally(() => setLoading(false));
  }, [token, jobId]);

  const handleStatusChange = async (appId, status) => {
    try {
      await updateApplicationStatus(token, appId, status);
      setApplications((prev) =>
        prev.map((app) => (app.id === appId ? { ...app, status } : app))
      );
      setSuccess(`Status updated to ${status}.`);
      setError("");
      setTimeout(() => setSuccess(""), 2000);
    } catch (e) {
      setError("Failed to update status.");
    }
  };

  const handleExportPDF = async () => {
    try {
      const res = await exportApplicationsPDF(token, jobId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url);
    } catch {
      setError("Unable to export PDF.");
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await exportApplicationsExcel(token, jobId);
      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      );
      window.open(url);
    } catch {
      setError("Unable to export Excel.");
    }
  };

  const renderDecisionCell = (app) => {
    if (app.status === "SHORTLISTED") return <span className="ma-status ma-status--pending">Shortlisted</span>;
    if (app.status === "REJECTED") return <span className="ma-status ma-status--rejected">Rejected</span>;
    if (app.status === "ACCEPTED") return <span className="ma-status ma-status--accepted">Hired</span>;
    if (app.status === "WITHDRAWN") return <span className="ma-status ma-status--withdrawn">Withdrawn</span>;
    return <span className="ma-status">{app.status || "—"}</span>;
  };

  const canDecide = (status) => !["SHORTLISTED", "REJECTED", "ACCEPTED"].includes(status);

  return (
    <>     
     <Header />
    <div className="ma-shell">
      <div className="ma-card">
        <div className="ma-head">
          <div>
            <h2 className="ma-title">Submitted Applications</h2>
            <p className="ma-subtitle">Manage candidates for this job posting</p>
          </div>

          <div className="ma-actionsTop">
            <button className="ma-btn ma-btn--ghost" type="button" onClick={() => navigate(-1)}>
              Back
            </button>

            <button className="ma-btn ma-btn--primary" type="button" onClick={handleExportPDF}>
              Export PDF
            </button>

            <button className="ma-btn ma-btn--ghost" type="button" onClick={handleExportExcel}>
              Export Excel
            </button>
          </div>
        </div>

        {success && <div className="ma-alert ma-alert--success">{success}</div>}
        {error && <div className="ma-alert ma-alert--error">{error}</div>}

        {loading ? (
          <div className="ma-empty">Loading...</div>
        ) : applications.length === 0 ? (
          <div className="ma-empty">No applications found</div>
        ) : (
          <div className="ma-tableWrap">
            <table className="ma-table">
              <thead>
                <tr>
                  <th>Candidate Name</th>
                  <th>Status</th>
                  <th>Date Applied</th>
                  <th className="ma-actionsCol">Actions</th>
                </tr>
              </thead>

              <tbody>
                {applications.map((app) => {
                  const candidateName =
                    `${app.candidate?.firstName || ""} ${app.candidate?.lastName || ""}`.trim() ||
                    "—";

                  return (
                    <tr key={app.id}>
                      <td className="ma-jobTitle">{candidateName}</td>

                      <td>{renderDecisionCell(app)}</td>

                      <td>{app.createdAt ? new Date(app.createdAt).toLocaleDateString() : "—"}</td>

                      <td className="ma-actions">
                        {/* Always visible */}
                        <button
                          className="ma-btn ma-btn--primary"
                          type="button"
                          onClick={() => navigate(`/applications/${app.id}`)}
                        >
                          Screening
                        </button>

                        {/* Only show decision buttons if not already decided */}
                        {canDecide(app.status) ? (
                          <>
                            <button
                              className="ma-btn ma-btn--ghost"
                              type="button"
                              onClick={() => handleStatusChange(app.id, "SHORTLISTED")}
                            >
                              Shortlist
                            </button>

                            <button
                              className="ma-btn ma-btn--danger"
                              type="button"
                              onClick={() => handleStatusChange(app.id, "REJECTED")}
                            >
                              Reject
                            </button>

                            <button
                              className="ma-btn ma-btn--ghost"
                              type="button"
                              onClick={() => handleStatusChange(app.id, "ACCEPTED")}
                            >
                              Hire
                            </button>
                          </>
                        ) : (
                          <button className="ma-btn ma-btn--ghost" type="button" disabled>
                            Decision: {app.status}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default SubmittedApplications;
